"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { deleteErrorLogs, getErrorLogs, type GetErrorLogsOptions } from "@/lib/error-log-service";
import type { ErrorLog } from "@ezrecipebox/contracts";
import { AlertCircle, AlertTriangle, ChevronRight, Info, Loader2, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

export function LogViewerGrid() {
  const { toast } = useToast();
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastId, setLastId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [logTypeFilter, setLogTypeFilter] = useState<"error" | "warning" | "info" | "all">("all");
  const [platformFilter, setPlatformFilter] = useState<string>("all");
  const [pageSize, setPageSize] = useState<number>(25);
  const [selectedLogs, setSelectedLogs] = useState<Set<string>>(new Set());
  const [viewingLog, setViewingLog] = useState<ErrorLog | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadLogs = useCallback(
    async (reset = false) => {
      setLoading(true);
      setError(null);

      try {
        const options: GetErrorLogsOptions = {
          pageSize,
          searchQuery: searchQuery || undefined,
          logType: logTypeFilter,
          platform: platformFilter === "all" ? undefined : platformFilter,
          startAfterId: reset ? undefined : (lastId ?? undefined),
        };

        const result = await getErrorLogs(options);
        setLogs((prev) => (reset ? result.logs : [...prev, ...result.logs]));
        setLastId(result.lastId);
        setHasMore(result.hasMore);
        setTotal(result.total);
        if (reset) {
          setSelectedLogs(new Set());
        }
      } catch (err) {
        console.error("Failed to load error logs", err);
        setError(err instanceof Error ? err.message : "Failed to load error logs");
        toast({
          title: "Error",
          description: "Failed to load error logs",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, logTypeFilter, platformFilter, pageSize, lastId],
  );

  useEffect(() => {
    loadLogs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, logTypeFilter, platformFilter, pageSize]);

  const handleDelete = async () => {
    if (selectedLogs.size === 0) return;

    setDeleting(true);
    try {
      const logIds = Array.from(selectedLogs);
      await deleteErrorLogs(logIds);
      toast({
        title: "Success",
        description: `Deleted ${logIds.length} log${logIds.length > 1 ? 's' : ''}`,
      });
      setSelectedLogs(new Set());
      loadLogs(true);
    } catch (err) {
      console.error("Failed to delete logs", err);
      toast({
        title: "Error",
        description: "Failed to delete logs",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedLogs.size === logs.length) {
      setSelectedLogs(new Set());
    } else {
      setSelectedLogs(new Set(logs.map(log => log.id)));
    }
  };

  const toggleSelectLog = (logId: string) => {
    const newSelected = new Set(selectedLogs);
    if (newSelected.has(logId)) {
      newSelected.delete(logId);
    } else {
      newSelected.add(logId);
    }
    setSelectedLogs(newSelected);
  };

  const getLogIcon = (type: string) => {
    switch (type) {
      case "error":
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case "info":
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return null;
    }
  };

  const getLogBadgeVariant = (type: string): "destructive" | "default" | "secondary" => {
    switch (type) {
      case "error":
        return "destructive";
      case "warning":
        return "default";
      case "info":
        return "secondary";
      default:
        return "default";
    }
  };

  const formatTimestamp = (timestamp: unknown) => {
    try {
      let date: Date;
      
      // Handle ISO strings (from backend)
      if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      }
      // Handle Firestore Timestamp with toDate() method
      else if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
        date = (timestamp as { toDate: () => Date }).toDate();
      }
      // Handle Date objects
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      // Handle numeric timestamps (unix time in ms)
      else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else {
        return 'Invalid date';
      }

      // Verify date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }

      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return 'Invalid date';
    }
  };

  // Get unique platforms for filter
  const platforms = ["all", ...Array.from(new Set(logs.map(log => log.platform)))];

  return (
    <div className="space-y-4">
      {/* Filters and Search */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by context, error, user email, function..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
          <Select value={logTypeFilter} onValueChange={(value) => setLogTypeFilter(value as typeof logTypeFilter)}>
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="error">Errors</SelectItem>
              <SelectItem value="warning">Warnings</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Platform" />
            </SelectTrigger>
            <SelectContent>
              {platforms.map(platform => (
                <SelectItem key={platform} value={platform}>
                  {platform === "all" ? "All Platforms" : platform}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number.parseInt(value, 10))}>
            <SelectTrigger className="w-full sm:w-[120px]">
              <SelectValue placeholder="Page Size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 rows</SelectItem>
              <SelectItem value="25">25 rows</SelectItem>
              <SelectItem value="50">50 rows</SelectItem>
              <SelectItem value="100">100 rows</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="destructive"
              size="sm"
              disabled={selectedLogs.size === 0 || deleting}
              onClick={handleDelete}
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedLogs.size})
                </>
              )}
            </Button>
          </div>
          <div className="text-sm text-muted-foreground">
            Showing {logs.length} of {total} logs
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Logs Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox
                  checked={logs.length > 0 && selectedLogs.size === logs.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Context</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Timestamp</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && logs.length === 0 ? (
              Array.from({ length: pageSize }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                </TableRow>
              ))
            ) : (
              logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No logs found
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedLogs.has(log.id)}
                      onCheckedChange={() => toggleSelectLog(log.id)}
                      aria-label={`Select log ${log.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getLogIcon(log.type)}
                      <Badge variant={getLogBadgeVariant(log.type)} className="text-xs">
                        {log.type}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs max-w-[200px] truncate">
                    {log.context}
                  </TableCell>
                  <TableCell className="max-w-[300px] truncate">
                    {log.error || log.message || '-'}
                  </TableCell>
                  <TableCell className="text-xs">
                    {log.userEmail ? (
                      <div className="max-w-[150px] truncate" title={log.userEmail}>
                        {log.userEmail}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Anonymous</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="max-w-[120px] truncate" title={log.platform}>
                      {log.platform}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatTimestamp(log.timestamp)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewingLog(log)}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
              )
            )}
          </TableBody>
        </Table>
      </div>

      {/* Load More */}
      {hasMore && !loading && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            onClick={() => loadLogs(false)}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!viewingLog} onOpenChange={(open) => !open && setViewingLog(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewingLog && getLogIcon(viewingLog.type)}
              Log Details
            </DialogTitle>
            <DialogDescription>
              {viewingLog && formatTimestamp(viewingLog.timestamp)}
            </DialogDescription>
          </DialogHeader>
          {viewingLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium mb-1">Type</div>
                  <Badge variant={getLogBadgeVariant(viewingLog.type)}>
                    {viewingLog.type}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1">Platform</div>
                  <div className="text-sm text-muted-foreground">{viewingLog.platform} {viewingLog.platformVersion}</div>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1">Context</div>
                  <div className="text-sm font-mono bg-muted p-2 rounded">{viewingLog.context}</div>
                </div>
                <div>
                  <div className="text-sm font-medium mb-1">User</div>
                  <div className="text-sm text-muted-foreground">
                    {viewingLog.userEmail || 'Anonymous'}
                    {viewingLog.userId && (
                      <div className="text-xs font-mono mt-1">{viewingLog.userId}</div>
                    )}
                  </div>
                </div>
              </div>

              {viewingLog.functionName && (
                <div>
                  <div className="text-sm font-medium mb-1">Function</div>
                  <div className="text-sm font-mono bg-muted p-2 rounded">
                    {viewingLog.functionName}
                    {viewingLog.region && ` (${viewingLog.region})`}
                  </div>
                </div>
              )}

              {viewingLog.userAgent && (
                <div>
                  <div className="text-sm font-medium mb-1">User Agent</div>
                  <div className="text-xs text-muted-foreground break-all">{viewingLog.userAgent}</div>
                </div>
              )}

              {(viewingLog.error || viewingLog.message) && (
                <div>
                  <div className="text-sm font-medium mb-1">
                    {viewingLog.error ? 'Error' : 'Message'}
                  </div>
                  <div className="text-sm bg-muted p-3 rounded font-mono break-words">
                    {viewingLog.error || viewingLog.message}
                  </div>
                </div>
              )}

              {viewingLog.stackTrace && (
                <div>
                  <div className="text-sm font-medium mb-1">Stack Trace</div>
                  <div className="text-xs bg-muted p-3 rounded font-mono whitespace-pre-wrap overflow-x-auto">
                    {viewingLog.stackTrace}
                  </div>
                </div>
              )}

              {viewingLog.additionalData && Object.keys(viewingLog.additionalData).length > 0 && (
                <div>
                  <div className="text-sm font-medium mb-1">Additional Data</div>
                  <div className="text-xs bg-muted p-3 rounded font-mono whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(viewingLog.additionalData, null, 2)}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingLog(null)}>
              Close
            </Button>
            {viewingLog && (
              <Button
                variant="destructive"
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await deleteErrorLogs([viewingLog.id]);
                    toast({
                      title: "Success",
                      description: "Log deleted",
                    });
                    setViewingLog(null);
                    loadLogs(true);
                  } catch (err) {
                    console.error("Failed to delete log", err);
                    toast({
                      title: "Error",
                      description: "Failed to delete log",
                      variant: "destructive",
                    });
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
